import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../context/GameContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiRefreshCw } = FiIcons;

function LogicGame() {
  const navigate = useNavigate();
  const { state, dispatch } = useGame();
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [questionsCompleted, setQuestionsCompleted] = useState(0);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [level, setLevel] = useState(1);

  const logicQuestions = {
    easy: [
      {
        question: "If all cats have tails, and Fluffy is a cat, what can we say about Fluffy?",
        answers: ["Fluffy has a tail", "Fluffy has no tail", "Fluffy might have a tail", "We can't tell"],
        correct: 0,
        explanation: "Since all cats have tails and Fluffy is a cat, Fluffy must have a tail!"
      },
      {
        question: "What comes next in this sequence: 🔴🔵🔴🔵🔴?",
        answers: ["🔴", "🔵", "🟡", "🟢"],
        correct: 1,
        explanation: "The pattern alternates between red and blue circles!"
      },
      {
        question: "If it's raining, then the ground gets wet. It's raining. What happens?",
        answers: ["The ground gets wet", "The ground stays dry", "The sun comes out", "Nothing happens"],
        correct: 0,
        explanation: "When it rains, the ground always gets wet!"
      }
    ],
    medium: [
      {
        question: "All birds can fly. Penguins are birds. Can penguins fly?",
        answers: ["Yes, always", "No, this is a trick question", "Only in water", "Sometimes"],
        correct: 1,
        explanation: "This is a logic puzzle! In reality, not all birds can fly - penguins can't!"
      },
      {
        question: "If A > B and B > C, what can we say about A and C?",
        answers: ["A > C", "A < C", "A = C", "We can't tell"],
        correct: 0,
        explanation: "If A is greater than B, and B is greater than C, then A must be greater than C!"
      },
      {
        question: "What's the missing number: 2, 4, 8, 16, ?",
        answers: ["24", "32", "20", "18"],
        correct: 1,
        explanation: "Each number is doubled: 2×2=4, 4×2=8, 8×2=16, 16×2=32!"
      }
    ],
    hard: [
      {
        question: "If some cats are black, and some black things are cars, can we conclude that some cats are cars?",
        answers: ["Yes", "No", "Maybe", "Only on Sundays"],
        correct: 1,
        explanation: "No! Just because cats and cars can both be black doesn't mean cats are cars!"
      },
      {
        question: "In a race, you overtake the person in 2nd place. What position are you in now?",
        answers: ["1st place", "2nd place", "3rd place", "Last place"],
        correct: 1,
        explanation: "If you overtake 2nd place, you take their position - you're now in 2nd place!"
      },
      {
        question: "What's the next number in this sequence: 1, 1, 2, 3, 5, 8, ?",
        answers: ["11", "13", "15", "16"],
        correct: 1,
        explanation: "This is the Fibonacci sequence! Each number is the sum of the two before it: 5+8=13!"
      }
    ]
  };

  const getDifficultyLevel = () => {
    if (level <= 7) return 'easy';
    if (level <= 14) return 'medium';
    return 'hard';
  };

  const generateQuestion = () => {
    const difficulty = getDifficultyLevel();
    const questions = logicQuestions[difficulty];
    return questions[Math.floor(Math.random() * questions.length)];
  };

  useEffect(() => {
    setCurrentQuestion(generateQuestion());
  }, [level]);

  const handleAnswerSelect = (answerIndex) => {
    setSelectedAnswer(answerIndex);
    setShowResult(true);

    const isCorrect = answerIndex === currentQuestion.correct;
    if (isCorrect) {
      dispatch({ type: 'ADD_SCORE', payload: 25 });
    } else {
      dispatch({ type: 'ADD_WRONG_ANSWER' });
    }

    setTimeout(() => {
      const newCompleted = questionsCompleted + 1;
      setQuestionsCompleted(newCompleted);
      
      if (newCompleted >= 5) {
        setGameCompleted(true);
        dispatch({ type: 'GAME_COMPLETED' });
      } else {
        setCurrentQuestion(generateQuestion());
        setSelectedAnswer(null);
        setShowResult(false);
      }
    }, 3000);
  };

  const restartGame = () => {
    setCurrentQuestion(generateQuestion());
    setSelectedAnswer(null);
    setShowResult(false);
    setQuestionsCompleted(0);
    setGameCompleted(false);
  };

  const nextLevel = () => {
    setLevel(prev => prev + 1);
    setQuestionsCompleted(0);
    setGameCompleted(false);
    setCurrentQuestion(generateQuestion());
    setSelectedAnswer(null);
    setShowResult(false);
  };

  if (!currentQuestion && !gameCompleted) {
    return <div>Loading...</div>;
  }

  if (gameCompleted) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen flex items-center justify-center p-4"
      >
        <div className="bg-white rounded-3xl p-8 shadow-2xl border-4 border-safari-200 text-center max-w-md">
          <div className="text-6xl mb-4">🧩</div>
          <h2 className="text-3xl font-kid text-jungle-700 mb-4">
            Logic Master!
          </h2>
          <p className="font-comic text-jungle-600 mb-6">
            Level {level} completed! Your logical thinking is excellent!
          </p>
          <div className="space-y-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={nextLevel}
              className="w-full bg-gradient-to-r from-indigo-500 to-indigo-600 text-white p-3 rounded-2xl font-kid text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Next Level
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={restartGame}
              className="w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white p-3 rounded-2xl font-kid text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Play Again
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/dashboard')}
              className="w-full bg-gradient-to-r from-safari-500 to-safari-600 text-white p-3 rounded-2xl font-kid text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Back to Games
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen p-4"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="flex items-center justify-between mb-8"
        >
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2 bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200 hover:shadow-xl transition-all duration-200"
          >
            <SafeIcon icon={FiArrowLeft} className="text-jungle-600" />
            <span className="font-comic text-jungle-700">Back</span>
          </button>

          <div className="text-center">
            <h1 className="text-3xl font-kid text-jungle-700">Logic Puzzles 🧩</h1>
            <p className="font-comic text-jungle-600">Level {level} - {getDifficultyLevel().toUpperCase()}</p>
          </div>

          <button
            onClick={restartGame}
            className="flex items-center space-x-2 bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200 hover:shadow-xl transition-all duration-200"
          >
            <SafeIcon icon={FiRefreshCw} className="text-jungle-600" />
            <span className="font-comic text-jungle-700">Restart</span>
          </button>
        </motion.div>

        {/* Progress */}
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${(questionsCompleted / 5) * 100}%` }}
          className="h-3 bg-indigo-400 rounded-full mb-8 shadow-inner"
        >
          <div className="w-full h-full bg-gradient-to-r from-indigo-400 to-indigo-500 rounded-full"></div>
        </motion.div>

        {/* Question */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-3xl p-8 shadow-xl border-4 border-safari-200 mb-8"
        >
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              className="text-6xl mb-4"
            >
              🤔
            </motion.div>
            <h2 className="text-xl font-comic text-jungle-700 mb-4 leading-relaxed">
              {currentQuestion.question}
            </h2>
          </div>

          {/* Answer Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {currentQuestion.answers.map((answer, index) => (
              <motion.button
                key={index}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => !showResult && handleAnswerSelect(index)}
                disabled={showResult}
                className={`p-6 rounded-2xl font-comic text-lg shadow-lg transition-all duration-200 ${
                  showResult
                    ? index === currentQuestion.correct
                      ? 'bg-green-400 text-white'
                      : index === selectedAnswer
                      ? 'bg-red-400 text-white'
                      : 'bg-gray-200 text-gray-500'
                    : 'bg-gradient-to-br from-safari-200 to-safari-300 text-jungle-700 hover:from-safari-300 hover:to-safari-400'
                }`}
              >
                {answer}
              </motion.button>
            ))}
          </div>

          {/* Result Message */}
          <AnimatePresence>
            {showResult && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="mt-6 text-center"
              >
                {selectedAnswer === currentQuestion.correct ? (
                  <div className="text-green-600 font-kid text-xl mb-2">
                    🎉 Excellent reasoning!
                  </div>
                ) : (
                  <div className="text-red-600 font-kid text-xl mb-2">
                    Good try! Think about it more...
                  </div>
                )}
                <p className="font-comic text-jungle-600 text-base">
                  {currentQuestion.explanation}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default LogicGame;